<template>
    <div class="projects-container">
        <div class="hero-section">
            <h1 class="page-title">
                <span class="gradient-text">Projects</span>
                <div class="title-underline"></div>
            </h1>
        </div>

        <!-- VideoGame Projects Section -->
        <section class="projects-section">
            <div class="section-header">
                <h2 class="section-title">VideoGame Projects</h2>
                <p class="section-description">
                    These are the videogame projects I have worked on, including the ones done for my courses.
                    Some of them are prototypes or unfinished games, and others are only small games that try to hone a specific skill.
                    There may also be games that are currently in development, and will be updated as they are finished.
                </p>
            </div>

            <div class="projects-grid">
                <!-- AI for Games Projects -->
                <div class="project-card">
                    <div class="project-content">
                        <h3 class="project-title">AI for Games Projects</h3>
                        <p class="project-description">
                            Even though these are 3 different projects, I decided to put them together since they all have the same goal: to create an AI for a game.
                            The first project is about Pathfinding, the second about Decision-Making and the third about Reinforcement Learning.
                        </p>
                        <div class="project-showcase">
                            <div class="showcase-item">
                                <a href="https://github.com/fallenatlas/Project1.IAJ_23-24" class="project-link">
                                    <img src="../assets/images/ai/pathfinding.png" alt="Pathfinding" class="project-image">
                                    <span class="project-label">Project 1 - Pathfinding</span>
                                </a>
                            </div>
                            <div class="showcase-item">
                                <a href="https://github.com/SebastiaoCarvalho/Project2.IAJ_23-24" class="project-link">
                                    <img src="../assets/images/ai/pathfinding.png" alt="Decision-Making" class="project-image">
                                    <span class="project-label">Project 2 - Decision-Making</span>
                                </a>
                            </div>
                            <div class="showcase-item">
                                <a href="https://github.com/SebastiaoCarvalho/Project3.IAJ_23-24" class="project-link">
                                    <img src="../assets/images/ai/pathfinding.png" alt="Reinforcement Learning" class="project-image">
                                    <span class="project-label">Project 3 - Reinforcement Learning</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Game Development Project -->
                <div class="project-card">
                    <div class="project-content">
                        <h3 class="project-title">Game Development Methodologies Project</h3>
                        <p class="project-description">
                            For the Game Development Methodologies course, I worked with one Artist, one Game Designer and one Sound Designer to create a game
                            based on a movie or tv series. We chose the cartoon "Tom and Jerry" and created a 2-player game where one player controls Tom 
                            and the other controls Jerry. The goal for Tom is to catch Jerry as fast as possible, while Jerry has to escape and collect cheese.
                        </p>
                        <p class="project-description">
                            The game was developed using the Godot engine. The game is 2D with a front-view perspective. The final product is a
                            completely finished game, and you can find more about the game 
                            <a class="inline-link" href="https://sebastiaocarvalho.itch.io/cheese-in-the-trap">here</a>
                            . The source code can be found 
                            <a class="inline-link" href="https://github.com/footvaalvica/CheeseInTheTrap">here</a>.
                        </p>
                        <div class="project-showcase">
                            <div class="showcase-item">
                                <a href="https://sebastiaocarvalho.itch.io/cheese-in-the-trap" class="project-link">
                                    <img src="../assets/images/mdj/title.png" alt="Title" class="project-image">
                                    <span class="project-label">Title Screen</span>
                                </a>
                            </div>
                            <div class="showcase-item">
                                <a href="https://sebastiaocarvalho.itch.io/cheese-in-the-trap" class="project-link">
                                    <img src="../assets/images/mdj/controls.png" alt="Controls" class="project-image">
                                    <span class="project-label">Game Controls</span>
                                </a>
                            </div>
                            <div class="showcase-item">
                                <a href="https://sebastiaocarvalho.itch.io/cheese-in-the-trap" class="project-link">
                                    <img src="../assets/images/mdj/game.png" alt="Gameplay" class="project-image">
                                    <span class="project-label">Gameplay Capture</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Game Design Project -->
                <div class="project-card">
                    <div class="project-content">
                        <h3 class="project-title">Game Design Project</h3>
                        <p class="project-description">
                            For my Game Design course, my team of 3 people had to create a game from scratch that followed the theme "10 seconds of space".
                            We decided to create a game where you had to survive for 10 seconds in space, and had to reach checkpoints to get more time.
                        </p>
                        <p class="project-description">
                            The game was made in Unity, and we had to create almost all the assets ourselves. 
                            Our final product is a 3D game where you control an astronaut (capsule), reach new checkpoints, complete tasks
                            and much more in order to escape a deadly mist that is slowly killing you. You can find the source code 
                            <a class="inline-link" href="https://github.com/SebastiaoCarvalho/Escape-The-Mist-Of-Time">here</a> 
                            and a build of the game 
                            <a class="inline-link" href="https://chiefbat.itch.io/escape-the-mist-of-time">here</a>.
                        </p>
                        <div class="project-showcase">
                            <div class="showcase-item">
                                <a href="https://github.com/SebastiaoCarvalho/Escape-The-Mist-Of-Time" class="project-link">
                                    <img src="../assets/images/gameDesign/Map.png" alt="Map" class="project-image">
                                    <span class="project-label">Main Screen</span>
                                </a>
                            </div>
                            <div class="showcase-item">
                                <a href="https://github.com/SebastiaoCarvalho/Escape-The-Mist-Of-Time" class="project-link">
                                    <img src="../assets/images/gameDesign/Map.png" alt="Map" class="project-image">
                                    <span class="project-label">Planning Menu - Map</span>
                                </a>
                            </div>
                            <div class="showcase-item">
                                <a href="https://github.com/SebastiaoCarvalho/Escape-The-Mist-Of-Time" class="project-link">
                                    <img src="../assets/images/gameDesign/Capeta.png" alt="Capeta" class="project-image">
                                    <span class="project-label">Fight against Mr. Capeta</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Blind Gamers Jam 2024 -->
                <div class="project-card">
                    <div class="project-content">
                        <h3 class="project-title">Blind Gamers Jam 2024</h3>
                        <p class="project-description">
                            This was a game I made for Blind Gamers Jam 2024, as part of GameDev Técnico. The goal of the jam was to create a game that 
                            was accessible to blind people, so we had to think of a game that could be played without the need of sight.
                        </p>
                        <p class="project-description">
                            The game was made in Unity, and we had to create almost all the assets ourselves. 
                            It's a game where you need to escape a maze, but you can't see anything. You have to follow a music, solve
                            sound puzzles and use your dog to find clues if you're stuck. The final product is fully playable.
                            You can find the source code 
                            <a class="inline-link" href="https://github.com/SebastiaoCarvalho/Blind-Gamers-Jam">here</a> 
                            and a build of the game 
                            <a class="inline-link" href="https://sebastiaocarvalho.itch.io/turn-off-the-song">here</a>.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Side Projects Section -->
        <section class="projects-section">
            <div class="section-header">
                <h2 class="section-title">Side Projects</h2>
                <p class="section-description">
                    These are some side projects that I did for fun or because I thought they would be useful. They are open source
                    and available for anyone to use. Some of them are still in development, and will be updated as I add more features.
                </p>
            </div>

            <div class="projects-grid">

                <!-- PsPlus API -->
                <div class="project-card">
                    <div class="project-content">
                        <h3 class="project-title">PsPlus API</h3>
                        <p class="project-description">
                            This is an API that I created to integrate with my FreeGames bot. It's a simple API that scrapes the Ps Plus website
                            and returns the current free games. It also returns the expiration date so you know how much time you have left to get the game.
                        </p>
                        <p class="project-description">
                            This project is still in development since I plan to add more endpoints for different types of PsPlus plans.
                            The site for the documentation can be found here (link coming soon). The source code can be found 
                            <a class="inline-link" href="https://github.com/SebastiaoCarvalho/psPlusApi">here</a>.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
export default {
    name: 'ProjectsView',
}
</script>

<style lang="scss" scoped>
.projects-container {
    min-height: 100vh;
    padding: 2rem 0;
    background: linear-gradient(135deg, var(--background-dark) 0%, var(--background-darker) 100%);
}

// Hero Section
.hero-section {
    text-align: center;
    margin-bottom: 4rem;
    
    .page-title {
        font-size: 4rem;
        font-weight: 800;
        margin-bottom: 1rem;
        position: relative;
        display: inline-block;
        animation: fadeInDown 1s ease-out;
        
        .gradient-text {
            background: var(--primary-gradient);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            position: relative;
            display: inline-block;
        }
        
        .title-underline {
            width: 100px;
            height: 4px;
            background: var(--primary-gradient);
            margin: 0.5rem auto 0;
            border-radius: 2px;
            animation: scaleIn 1s ease-out 0.5s both;
        }
    }
}

// Projects Section
.projects-section {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    margin-bottom: 5rem;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
    
    .section-title {
        font-size: 2.5rem;
        font-weight: 700;
        background: var(--accent-gradient);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        margin-bottom: 1rem;
        position: relative;
        display: inline-block;
    }
    
    .section-description {
        font-size: 1.2rem;
        color: var(--text-muted);
        line-height: 1.8;
        max-width: 800px;
        margin: 0 auto;
    }
}

// Projects Grid
.projects-grid {
    display: grid;
    gap: 2rem;
    grid-template-columns: 1fr;
}

// Project Cards
.project-card {
    background: linear-gradient(135deg, var(--background-card) 0%, rgba(26, 29, 35, 0.8) 100%);
    border-radius: 20px;
    padding: 2rem;
    box-shadow: var(--shadow-dark), var(--shadow-glow);
    border: 1px solid var(--border-color);
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--primary-gradient);
        transform: scaleX(0);
        transition: transform 0.3s ease;
    }
    
    &:hover {
        transform: translateY(-5px);
        box-shadow: var(--shadow-hover), 0 0 40px rgba(0, 212, 255, 0.15);
        border-color: var(--border-glow);
        
        &::before {
            transform: scaleX(1);
        }
    }
}

.project-content {
    .project-title {
        font-size: 1.8rem;
        font-weight: 600;
        color: var(--text-bright);
        margin-bottom: 1rem;
        position: relative;
        
        &::after {
            content: '';
            position: absolute;
            bottom: -0.5rem;
            left: 0;
            width: 50px;
            height: 2px;
            background: var(--accent-gradient);
            border-radius: 1px;
        }
    }
    
    .project-description {
        font-size: 1rem;
        color: var(--text-muted);
        line-height: 1.7;
        margin-bottom: 1.5rem;
        text-align: left;
    }
}

// Project Showcase
.project-showcase {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin-top: 2rem;
}

.showcase-item {
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    
    .project-link {
        display: block;
        text-decoration: none;
        color: inherit;
        transition: all 0.3s ease;
        
        &:hover {
            transform: scale(1.05);
            
            .project-image {
                filter: brightness(1.2);
            }
            
            .project-label {
                background: var(--primary-gradient);
                color: var(--text-bright);
            }
        }
    }
    
    .project-image {
        width: 100%;
        height: 150px;
        object-fit: cover;
        border-radius: 8px;
        border: 2px solid var(--border-color);
        transition: all 0.3s ease;
        filter: brightness(0.9);
    }
    
    .project-label {
        display: block;
        text-align: center;
        margin-top: 0.5rem;
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--text-muted);
        padding: 0.5rem;
        background: rgba(26, 29, 35, 0.8);
        border-radius: 8px;
        transition: all 0.3s ease;
    }
}

// Inline Links
.inline-link {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 600;
    position: relative;
    transition: all 0.3s ease;
    
    &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 0;
        height: 2px;
        background: var(--primary-gradient);
        transition: width 0.3s ease;
    }
    
    &:hover {
        color: var(--text-bright);
        
        &::after {
            width: 100%;
        }
    }
}

// Animations
@keyframes fadeInDown {
    from {
        opacity: 0;
        transform: translateY(-30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes scaleIn {
    from {
        opacity: 0;
        transform: scaleX(0);
    }
    to {
        opacity: 1;
        transform: scaleX(1);
    }
}

// Responsive Design
@media (max-width: 768px) {
    .projects-container {
        padding: 1rem 0;
    }
    
    .hero-section .page-title {
        font-size: 3rem;
    }
    
    .section-header .section-title {
        font-size: 2rem;
    }
    
    .projects-section {
        padding: 0 1rem;
    }
    
    .project-card {
        padding: 1.5rem;
    }
    
    .project-showcase {
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 1rem;
    }
    
    .showcase-item .project-image {
        height: 120px;
    }
}

@media (max-width: 480px) {
    .hero-section .page-title {
        font-size: 2.5rem;
    }
    
    .section-header .section-title {
        font-size: 1.8rem;
    }
    
    .project-content .project-title {
        font-size: 1.5rem;
    }
    
    .project-showcase {
        grid-template-columns: 1fr;
    }
}
</style>